import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NursingHome } from "@/hooks/use-nursing-home";

interface RoomsFormData {
  roomTypes: string[];
  customRoomTypes: string[];
  roomSizes: { type: string; size_sqm?: number; weekly_price?: number; features?: string[] }[];
  deposit_amount?: number | string;
}

interface RoomsFormProps {
  nursingHome: NursingHome;
  onSave: (data: Partial<NursingHome>) => Promise<NursingHome>;
  isSaving: boolean;
}

export function RoomsForm({ nursingHome, onSave, isSaving }: RoomsFormProps) {
  const { register, handleSubmit, setValue } = useForm<RoomsFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: RoomsFormData) => {
    try {
      setIsSubmitting(true);
      
      // Filter out empty room sizes
      const filteredRoomSizes = data.roomSizes?.filter(room => 
        room.type && (room.size_sqm || room.weekly_price || room.features)
      ) || [];
      
      // Prepare room types array (combining default and custom)
      const roomTypes = [
        ...(data.roomTypes || []),
        ...(data.customRoomTypes?.filter(rt => rt.trim()) || [])
      ];
      
      // Save data excluding the custom fields
      const { customRoomTypes, ...restData } = data;
      
      // Convert deposit_amount to number if it's a string
      const depositAmount = restData.deposit_amount ? 
        typeof restData.deposit_amount === 'string' ? 
          parseFloat(restData.deposit_amount) : restData.deposit_amount 
        : undefined;
      
      await onSave({
        ...restData,
        room_types: roomTypes,
        room_sizes: filteredRoomSizes.length > 0 ? filteredRoomSizes : undefined,
        deposit_amount: depositAmount,
      });
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label htmlFor="roomTypes">Room Types</Label>
        <Input
          id="roomTypes"
          {...register("roomTypes")}
          placeholder="Enter room types"
        />
      </div>
      <div>
        <Label htmlFor="customRoomTypes">Custom Room Types</Label>
        <Input
          id="customRoomTypes"
          {...register("customRoomTypes")}
          placeholder="Enter custom room types"
        />
      </div>
      <div>
        <Label htmlFor="roomSizes">Room Sizes</Label>
        <Input
          id="roomSizes"
          {...register("roomSizes")}
          placeholder="Enter room sizes"
        />
      </div>
      <div>
        <Label htmlFor="deposit_amount">Deposit Amount</Label>
        <Input
          id="deposit_amount"
          type="text"
          {...register("deposit_amount")}
          placeholder="Enter deposit amount"
        />
      </div>
      <Button type="submit" disabled={isSubmitting || isSaving}>
        {isSubmitting || isSaving ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
